<script setup>
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { picoSearch } from '@chatwoot/pico-search';

import Button from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ToolCard from './ToolCard.vue';
import Dropdown from 'next/wgpt/Dropdown/Dropdown.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import { computed, ref, watch } from 'vue';

const props = defineProps({
  toolSet: {
    type: Object,
    required: true,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  builtIn: {
    type: Boolean,
    default: false,
  },
  builtInTools: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'toggle',
  'createTool',
  'cloneTool',
  'editTool',
  'deleteTool',
  'activate',
  'deactivate',
  'toggleActivateTool',
  'export',
]);
const { t } = useI18n();
const getters = useStoreGetters();

const searchQuery = ref('');
const isFullyClosed = ref(!props.isExpanded);

const menuItems = computed(() => {
  const active = props.toolSet.active;
  return [
    {
      label: t(
        `WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.MENU.${active ? 'DEACTIVATE' : 'ACTIVATE'}`
      ),
      action: active ? 'deactivate' : 'activate',
      value: 'toggle',
    },
    {
      label: t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.MENU.EXPORT'),
      action: 'export',
      value: 'export',
    },
    {
      label: t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.MENU.EDIT'),
      action: 'edit',
      value: 'edit',
    },
    {
      label: t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.MENU.DELETE'),
      action: 'delete',
      value: 'delete',
    },
  ];
});

const tools = computed(() => {
  if (props.builtIn) return props.builtInTools;
  return getters['wgptBotTools/getTools'].value(props.toolSet.id);
});

const filteredTools = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return tools.value;
  return picoSearch(tools.value, query, ['name', 'description']);
});

watch(tools, newTools => {
  if (newTools.length <= 3) {
    searchQuery.value = '';
  }
});

watch(
  () => props.isExpanded,
  expanded => {
    if (!expanded) {
      searchQuery.value = '';
    }
  }
);

const activeBadgeClasses = computed(() => {
  return props.toolSet.active
    ? 'text-n-teal-11 bg-n-teal-3'
    : 'text-n-slate-11 bg-n-alpha-2';
});

const handleAction = (e, toggle) => {
  switch (e.action) {
    case 'edit':
      emit('edit');
      break;
    case 'delete':
      emit('delete');
      break;
    case 'activate':
      emit('activate');
      break;
    case 'deactivate':
      emit('deactivate');
      break;
    case 'export':
      emit('export');
      break;
    default:
      break;
  }
  toggle();
};
</script>

<template>
  <div
    class="my-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
  >
    <div
      class="flex items-center gap-4 px-6 py-5 transition-colors"
      :class="[
        tools.length ? 'cursor-pointer hover:bg-n-alpha-1' : '',
        isFullyClosed ? 'rounded-xl' : 'rounded-t-xl',
      ]"
      @click="tools.length && emit('toggle')"
    >
      <div
        class="size-10 flex-shrink-0 rounded-lg bg-n-alpha-3 flex items-center justify-center"
      >
        <Icon
          :icon="builtIn ? 'i-lucide-sparkles' : 'i-lucide-boxes'"
          class="size-5 text-n-slate-11"
        />
      </div>

      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <span class="text-heading-3 text-n-slate-12 truncate">
          {{ toolSet.name }}
        </span>
        <span class="text-body-main text-n-slate-11 truncate">
          {{ toolSet.description }}
        </span>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0" @click.stop>
        <span
          class="text-body-main text-n-slate-11 whitespace-nowrap cursor-pointer"
          @click="tools.length && emit('toggle')"
        >
          {{
            t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.TOOLS.COUNT', {
              count: tools.length,
            })
          }}
        </span>
        <template v-if="!builtIn">
          <div class="w-px h-3 bg-n-weak" />
          <span
            class="text-xs font-medium inline-flex items-center h-6 px-2 rounded-md"
            :class="activeBadgeClasses"
          >
            {{
              toolSet.active
                ? t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.STATUS.ACTIVE')
                : t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.STATUS.INACTIVE')
            }}
          </span>
          <div class="w-px h-3 bg-n-weak" />
          <Button
            v-tooltip.top="'Add tool'"
            icon="i-lucide-plus"
            slate
            sm
            @click="emit('createTool')"
          />
          <Dropdown align="end">
            <template #trigger="{ toggle }">
              <Button
                icon="i-lucide-ellipsis-vertical"
                slate
                sm
                @click="toggle"
              />
            </template>
            <template #default="{ toggle }">
              <DropdownMenu
                class="!static"
                :menu-items="menuItems"
                @action="e => handleAction(e, toggle)"
              />
            </template>
          </Dropdown>
        </template>
      </div>
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 transition-transform duration-200 flex-shrink-0"
        :class="[
          tools.length ? 'text-n-slate-11' : 'text-n-slate-8',
          { 'rotate-180': isExpanded && tools.length },
        ]"
      />
    </div>

    <transition
      enter-active-class="overflow-hidden transition-all duration-300 ease-out"
      leave-active-class="overflow-hidden transition-all duration-300 ease-in"
      enter-from-class="overflow-hidden opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[690px] sm:max-h-[470px] md:max-h-[410px]"
      leave-from-class="opacity-100 max-h-[690px] sm:max-h-[470px] md:max-h-[410px]"
      leave-to-class="overflow-hidden opacity-0 max-h-0"
      @before-enter="isFullyClosed = false"
      @after-leave="isFullyClosed = true"
    >
      <div v-show="isExpanded">
        <div class="border-t border-n-weak">
          <div v-if="tools.length > 3" class="px-6 py-4">
            <Input
              v-model="searchQuery"
              :placeholder="
                t(
                  'WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.TOOLS.SEARCH_PLACEHOLDER'
                )
              "
              class="group w-52 min-w-0 [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:!rounded-[0.625rem]"
              size="sm"
              type="search"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>
          <div class="flex flex-col">
            <ToolCard
              v-for="(tool, index) of filteredTools"
              :key="tool.id || tool.key"
              :tool="tool"
              :built-in="builtIn"
              :class="{
                'border-b border-n-weak': index < filteredTools.length - 1,
              }"
              @clone="emit('cloneTool', tool)"
              @edit="emit('editTool', tool)"
              @delete="emit('deleteTool', tool)"
              @toggle-active="
                e => emit('toggleActivateTool', { tool, isActive: e })
              "
            />
            <p
              v-if="searchQuery && !filteredTools.length"
              class="text-body-main text-n-slate-11 text-center py-6"
            >
              {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.CARD.TOOLS.NO_RESULTS') }}
            </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
